// SpeechToText.js
import { PiMicrophoneLight, PiUserSquare } from "react-icons/pi";
import React, { useState, useEffect } from 'react';
import { ReactMediaRecorder } from 'react-media-recorder';
import { ImSpinner2 } from 'react-icons/im'; // Spinner icon
import useSpeechToText from "../hooks/useSpeechToText";

/**
 * Utility Functions to Convert AudioBuffer to WAV Blob
 */

// Converts an AudioBuffer to a WAV ArrayBuffer
const audioBufferToWav = (buffer, opt = {}) => {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const format = opt.float32 ? 3 : 1;
  const bitDepth = format === 3 ? 32 : 16;

  let interleaved;
  if (numChannels === 2) {
    interleaved = interleave(buffer.getChannelData(0), buffer.getChannelData(1));
  } else {
    interleaved = buffer.getChannelData(0);
  }

  const wavBuffer = encodeWAV(interleaved, numChannels, sampleRate, bitDepth);
  return new Blob([wavBuffer], { type: 'audio/wav' });
};

const interleave = (inputL, inputR) => {
  const length = inputL.length + inputR.length;
  const result = new Float32Array(length);

  let index = 0,
    inputIndex = 0;

  while (index < length) {
    result[index++] = inputL[inputIndex];
    result[index++] = inputR[inputIndex];
    inputIndex++;
  }
  return result;
};

const encodeWAV = (samples, numChannels, sampleRate, bitDepth) => {
  const bytesPerSample = bitDepth / 8;
  const blockAlign = numChannels * bytesPerSample;

  const buffer = new ArrayBuffer(44 + samples.length * bytesPerSample);
  const view = new DataView(buffer);

  /* RIFF identifier */
  writeString(view, 0, 'RIFF');
  /* file length */
  view.setUint32(4, 36 + samples.length * bytesPerSample, true);
  /* RIFF type */
  writeString(view, 8, 'WAVE');
  /* format chunk identifier */
  writeString(view, 12, 'fmt ');
  /* format chunk length */
  view.setUint32(16, 16, true);
  /* sample format (raw) */
  view.setUint16(20, 1, true);
  /* channel count */
  view.setUint16(22, numChannels, true);
  /* sample rate */
  view.setUint32(24, sampleRate, true);
  /* byte rate (sample rate * block align) */
  view.setUint32(28, sampleRate * blockAlign, true);
  /* block align (channel count * bytes per sample) */
  view.setUint16(32, blockAlign, true);
  /* bits per sample */
  view.setUint16(34, bitDepth, true);
  /* data chunk identifier */
  writeString(view, 36, 'data');
  /* data chunk length */
  view.setUint32(40, samples.length * bytesPerSample, true);

  // Write the PCM samples
  if (bitDepth === 16) {
    floatTo16BitPCM(view, 44, samples);
  } else {
    floatTo32BitPCM(view, 44, samples);
  }

  return view;
};

const writeString = (view, offset, string) => {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
};

const floatTo16BitPCM = (output, offset, input) => {
  for (let i = 0; i < input.length; i++, offset += 2) {
    let s = Math.max(-1, Math.min(1, input[i]));
    output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
  }
};

const floatTo32BitPCM = (output, offset, input) => {
  for (let i = 0; i < input.length; i++, offset += 4) {
    output.setFloat32(offset, input[i], true);
  }
};

/**
 * Main SpeechToText Component
 */
const SpeechToText = ({ text, setText,recording,setRecording }) => {
  const { error, loading, uploadWavFile } = useSpeechToText({ text: text, setText: setText });
  useEffect(() => {
    console.log('loading',loading)
  },[loading])




  
  /**
   * Function to decode Ogg/Other Blob to AudioBuffer
   */
  const decodeAudioBlob = async (blob) => {
    const arrayBuffer = await blob.arrayBuffer();
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
    return audioBuffer;
  };

  /**
   * Function to resample AudioBuffer to target sample rate and channels
   */
  const resampleAudioBuffer = async (audioBuffer, targetSampleRate, targetChannels) => {
    const originalSampleRate = audioBuffer.sampleRate;
    const originalChannels = audioBuffer.numberOfChannels;

    // Create OfflineAudioContext for resampling
    const offlineCtx = new OfflineAudioContext(targetChannels, Math.ceil(audioBuffer.duration * targetSampleRate), targetSampleRate);

    // Create buffer source
    const source = offlineCtx.createBufferSource();
    source.buffer = audioBuffer;

    // Connect source to destination
    source.connect(offlineCtx.destination);
    source.start(0);

    // Render the audio
    const renderedBuffer = await offlineCtx.startRendering();
    return renderedBuffer;
  };

  /**
   * Function to convert AudioBuffer to WAV Blob
   */
  const audioBufferToWavBlob = (buffer, opt = {}) => {
    return audioBufferToWav(buffer, opt);
  };
  const [audioStream, setAudioStream] = useState(null);
return (
    <ReactMediaRecorder
    audio
    mimeType="audio/webm" // Use 'audio/webm' or 'audio/ogg' based on browser support
    onStop={async (blobUrl, blob) => {
      console.log('Audio Blob URL:', blobUrl); // Should start with "blob:"
      console.log('Audio Blob:', blob); // Should be a valid Blob object

      try {
        // Step 1: Decode the recorded audio Blob to AudioBuffer
        const audioBuffer = await decodeAudioBlob(blob);

        // Step 2: Resample to 16kHz and mono
        const targetSampleRate = 16000;
        const targetChannels = 1;
        const resampledBuffer = await resampleAudioBuffer(audioBuffer, targetSampleRate, targetChannels);

        // Step 3: Convert the resampled AudioBuffer to WAV Blob
        const wavBlob = audioBufferToWavBlob(resampledBuffer, { float32: false }); // 16-bit PCM
        uploadWavFile(wavBlob); // Call the function from the hook to upload the file

        console.log('WAV Blob:', wavBlob);
        console.log(`WAV Blob Size: ${wavBlob.size} bytes`);
        console.log(`WAV Blob Type: ${wavBlob.type}`);

        // Step 4: Pass the WAV Blob to Azure Speech-to-Text
        // const recognized =  await azureSpeechToText(wavBlob);
        // setText(recognized)

      } catch (error) {
        console.error('Error during audio conversion and transcription:', error);
      }
    }}
    render={({ status, startRecording, stopRecording, mediaBlobUrl }) => (
      <div>
        <div>
          <button  
          type="button" 
            onClick={() => {
             

                    
                  
                  
                  
                   

              if (recording) {
                setRecording(false);
                if (audioStream) {
                  const tracks = audioStream.getTracks();
                  tracks.forEach(track => track.stop()); // Stop all audio tracks
                  console.log('Microphone access disabled');
                }
                
                stopRecording(); // Start recording

              

              } else {
            
                setRecording(true);
                navigator.mediaDevices.getUserMedia({ audio: true })
                
                .then((stream) => {
                  
                        console.log('Microphone access granted');
                        setAudioStream(stream)
                  
                        // You can now use the microphone stream
                  
                      })
                  
                      .catch((error) => {
                  
                        console.error('Microphone access denied:', error);
                  
                      });

                startRecording(); // Start recording
              }
            }}
            style={{ background: 'transparent', border: 'none',  display: "flex",
              alignItems: "center",
              padding: "8px",}}
          >
            {loading ? 
                <ImSpinner2 className="w-4 h-5 animate-spin" /> 
              :
            <PiMicrophoneLight
              style={{
                color: recording ? '#007560' : '#4B5563', // Change color based on recording state
                fontSize: '1.25rem',
              }}
            />}
          </button>
        </div>
        <div>
        </div>
      </div>
    )}
  />
  );
};

/**
 * Inline Styles (optional)
 */

export default SpeechToText;




  